import React from "react";
import { Phone, MessageCircle, MapPin } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { CONTACT } from "@/data/site";
import { openWhatsApp } from "@/lib/wa";
import { TikTokIcon } from "./TikTokIcon";

// Discreet Eiffel Tower mark for the "Made in Paris" badge.
const Eiffel = ({ className = "" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 3h6" />
    <path d="M10.5 3c0 5-3 12-6.5 18" />
    <path d="M13.5 3c0 5 3 12 6.5 18" />
    <path d="M7.5 10h9" />
    <path d="M6 14h12" />
    <path d="M9.5 21h5" />
  </svg>
);

export default function Footer() {
  const { t } = useLang();
  return (
    <footer className="border-t border-[var(--gold)]/15 bg-[var(--bg-2)] pt-16" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="grid grid-cols-1 gap-12 pb-14 md:grid-cols-3">
          <div>
            <div className="font-serif text-3xl text-[var(--text)]">
              Le Goût <span className="italic text-gold">du Fouta</span>
            </div>
            <p className="mt-3 font-sans text-sm leading-relaxed text-[var(--text-2)]">
              {t.footerTagline}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-2.5">
              <span className="inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/40 px-3 py-1 font-sans text-[10px] uppercase tracking-[0.24em] text-[var(--gold)]">
                {t.halal}
              </span>
              <span
                data-testid="made-in-paris-badge"
                className="inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/40 bg-gradient-to-b from-[var(--gold)]/10 to-transparent px-3 py-1 font-sans text-[10px] uppercase tracking-[0.24em] text-[var(--gold-light)] transition-colors hover:border-[var(--gold)]"
              >
                <Eiffel className="h-3 w-3 text-[var(--gold)]" />
                <span>{t.madeIn}</span>
                <span className="h-1 w-1 rounded-full bg-[var(--gold)]/70" />
                <span className="text-[var(--gold)]/70">18<sup>e</sup></span>
              </span>
            </div>
          </div>

          <div>
            <p className="font-sans text-[11px] uppercase tracking-[0.24em] text-[var(--text-3)]">
              {t.hoursLabel}
            </p>
            <p className="mt-3 font-serif text-2xl italic text-[var(--text)]">{t.open}</p>
            <p className="font-sans text-sm text-[var(--text-2)]">{t.hours}</p>
            <a
              href={CONTACT.mapsLink}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 flex items-start gap-2 font-sans text-sm text-[var(--text-2)] transition-colors hover:text-[var(--gold)]"
              data-testid="footer-address"
            >
              <MapPin size={16} className="mt-0.5 shrink-0 text-[var(--gold)]" />
              <span>{CONTACT.address}, {CONTACT.city}<br />{CONTACT.metro}</span>
            </a>
          </div>

          <div className="flex flex-col gap-3">
            <a
              href={`tel:${CONTACT.phoneTel}`}
              className="flex items-center gap-3 border border-[var(--gold)]/30 px-5 py-3 font-sans text-sm text-[var(--text)] transition-colors hover:border-[var(--gold)]"
              data-testid="footer-call"
            >
              <Phone size={16} className="text-[var(--gold)]" /> {CONTACT.phoneDisplay}
            </a>
            <button
              onClick={() => openWhatsApp(t.waOrder)}
              className="flex items-center gap-3 border border-[var(--gold)]/30 px-5 py-3 font-sans text-sm text-[var(--text)] transition-colors hover:border-[var(--gold)]"
              data-testid="footer-whatsapp"
            >
              <MessageCircle size={16} className="text-[var(--gold)]" /> WhatsApp
            </button>
            <a
              href={CONTACT.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 border border-[var(--gold)]/30 px-5 py-3 font-sans text-sm text-[var(--text)] transition-colors hover:border-[var(--gold)]"
              data-testid="footer-tiktok"
            >
              <TikTokIcon size={16} className="text-[var(--gold)]" /> {CONTACT.tiktokHandle}
            </a>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-3 border-t border-white/5 py-6 sm:flex-row">
          <p className="font-sans text-xs text-[var(--text-3)]">
            © {new Date().getFullYear()} Le Goût du Fouta. {t.rights}
          </p>
          <p className="font-sans text-xs text-[var(--text-3)]">Paris 18e · France</p>
        </div>

        <div
          className="border-t border-white/5 py-5 text-center font-sans text-[11px] tracking-wide text-[var(--text-3)]"
          data-testid="agency-credit"
        >
          <span className="uppercase tracking-[0.18em]">Conception réalisée par</span>{" "}
          <span className="text-[var(--gold)]">Aiguel Web Agency</span> © 2026 — Tous droits réservés
          <span className="mx-2 text-[var(--gold)]/40">|</span>
          <a href="mailto:hello.aiguel@gmail.com" className="transition-colors hover:text-[var(--gold)]" data-testid="agency-email">
            hello.aiguel@gmail.com
          </a>
          <span className="mx-2 text-[var(--gold)]/40">|</span>
          <a href="https://www.aiguelweb.online" target="_blank" rel="noopener noreferrer" className="transition-colors hover:text-[var(--gold)]" data-testid="agency-site">
            www.aiguelweb.online
          </a>
        </div>
      </div>
    </footer>
  );
}
